import type { ReactNode } from "react"
import { Link, notFound } from "@tanstack/react-router"

import { Button } from "~/components/design-system/components"
import { NotFoundLayout } from "~/components/reusable/NotFound"
import { type api } from "~/utils/api"
import { isCollectionIdParam } from "~/utils/collection-route"

/** `params.parse` for a `$collectionId` route: a segment that can't name a collection is not found. */
export function parseCollectionIdParams({ collectionId }: { collectionId: string }) {
  if (!isCollectionIdParam(collectionId)) throw notFound()
  return { collectionId }
}

/**
 * Loader check that the collection exists. Seeds the cache the layout's context query reads.
 * A group route (`isGroup`) names a group id, not a collection, so it isn't checked here.
 */
export async function ensureCollectionExists(
  trpcUtils: ReturnType<typeof api.useUtils>,
  collectionId: string,
  isGroup: boolean,
) {
  if (isGroup) return
  const ctx = await trpcUtils.demand2.getCollectionContext.ensureData({
    collectionIds: [Number(collectionId)],
  })
  if (!ctx) throw notFound()
}

const CollectionNotFoundLayout = ({ back }: { back: ReactNode }) => (
  <NotFoundLayout
    title="Collection not found"
    description="This collection doesn't exist or was removed."
    action={back}
  />
)

export const CollectionNotFound = () => (
  <CollectionNotFoundLayout
    back={
      <Link to="/demand">
        <Button asChild>Back to Demand</Button>
      </Link>
    }
  />
)

export const DemandPlanningCollectionNotFound = () => (
  <CollectionNotFoundLayout
    back={
      <Link to="/demand-planning">
        <Button asChild>Back to Demand Planning</Button>
      </Link>
    }
  />
)
